/**
 * src/hooks/usePRTesting.js
 *
 * Fetches recently merged PRs from GitHub and tracks:
 *   - which PRs have been approved (verified as working)
 *   - manual sub-steps parsed from the PR body (## Manual steps section)
 *
 * Sub-steps are things like SQL migrations, Azure config changes, Cloudflare
 * updates — anything that can't be deployed via code alone. They are parsed
 * automatically from each PR's description and shown as checkboxes.
 *
 * Testing state (approved PRs + checked steps) is persisted server-side in
 * Azure SQL so it's shared across all devices and browsers.
 *
 * Only active in non-production environments (dev, test, localhost).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getFunctionUrl } from '../api/functionUrl.js';
import { getSessionToken } from '../api/azureClient.js';

const CACHE_TTL = 5 * 60 * 1000; // 5 min
const SAVE_DEBOUNCE = 800; // ms

/* ── module-level cache ── */
let _cache = null;
let _cacheTime = 0;

/* ── environment check ── */

export function isDevOrTestEnv() {
  const rid = (import.meta.env.VITE_REGISTRY_ID || '').toLowerCase();
  if (rid === 'registry-development' || rid === 'registry-test') return true;
  const h = window.location.hostname.toLowerCase();
  return h === 'localhost' || h === '127.0.0.1';
}

/** Returns true only for the development environment (not test). */
export function isDevEnvOnly() {
  const rid = (import.meta.env.VITE_REGISTRY_ID || '').toLowerCase();
  if (rid === 'registry-development') return true;
  const h = window.location.hostname.toLowerCase();
  return h === 'localhost' || h === '127.0.0.1';
}

/* ── Parse manual steps from PR body ── */

/**
 * Looks for a "## Manual steps" section in the PR description and extracts
 * checkbox items (`- [ ] text` or `- [x] text`). Returns array of strings.
 */
function parseManualSteps(body) {
  if (!body) return [];
  // Find the "Manual steps" section (case-insensitive)
  const sectionRegex = /^##\s*manual\s*steps?\s*$/im;
  const match = body.search(sectionRegex);
  if (match === -1) return [];

  // Get content from that header until the next ## header or end of body
  const afterHeader = body.slice(match);
  const lines = afterHeader.split('\n');
  const steps = [];

  // Skip the header line itself
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    // Stop at next section header
    if (/^##\s/.test(line)) break;
    // Match checkbox items: - [ ] text  or  - [x] text
    const checkboxMatch = line.match(/^[-*]\s*\[[ xX]?\]\s*(.+)/);
    if (checkboxMatch) {
      const text = checkboxMatch[1].trim();
      if (text) steps.push(text);
    }
  }
  return steps;
}

/* ── Parse "How to test" steps from PR body ── */

/**
 * Looks for a "## How to test" section and extracts plain list items
 * (`- text`, `* text`, or `1. text`). Returns array of strings.
 * These are PR-specific testing instructions written by Claude.
 */
export function parseHowToTest(body) {
  if (!body) return [];
  const sectionRegex = /^##\s*how\s+to\s+test\s*$/im;
  const match = body.search(sectionRegex);
  if (match === -1) return [];

  const afterHeader = body.slice(match);
  const lines = afterHeader.split('\n');
  const steps = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (/^##\s/.test(line)) break;
    // Match: "- text", "* text", "1. text", "1) text"
    const listMatch = line.match(/^[-*]\s+(.+)|^\d+[.)]\s+(.+)/);
    if (listMatch) {
      const text = (listMatch[1] || listMatch[2]).trim();
      if (text) steps.push(text);
    }
  }
  return steps;
}

/* ── Detect manual-action categories from manual steps only ── */

const SQL_RE =
  /\bsql\b|\.sql\b|\bmigrat(e|ion)s?\b|\brun\b.{0,30}\bquer(y|ies)\b|\balter\s+table\b|\bcreate\s+table\b/i;
const AZURE_RE = /\bazure\b|\bfunction\s*app\b|\bapp\s*setting|\bdeploy\b/i;
const CLOUDFLARE_RE = /\bcloudflare\b|\bcf\s*worker\b|\bdns\s*(record|change|update)/i;

/**
 * Detect categories from the parsed manual steps (not the full PR body).
 * Only shows badges when the PR author explicitly listed manual work.
 * Returns { sql, azure, cloudflare, other, sqlFiles[] }
 */
function detectCategories(steps) {
  const empty = { sql: false, azure: false, cloudflare: false, other: false, sqlFiles: [] };
  if (!steps || steps.length === 0) return empty;

  const stepsText = steps.join('\n');

  const sql = SQL_RE.test(stepsText);
  const azure = AZURE_RE.test(stepsText);
  const cloudflare = CLOUDFLARE_RE.test(stepsText);

  // Extract .sql file paths from manual steps only
  const sqlFiles = [];
  const fileRe = /`([^`]*\w[\w.-]*\.sql)`|(?:^|\s)((?:[\w./-]+\/)\w[\w.-]*\.sql)\b/gm;
  let m;
  while ((m = fileRe.exec(stepsText)) !== null) {
    const f = (m[1] || m[2] || '').trim();
    if (f && !sqlFiles.includes(f)) sqlFiles.push(f);
  }

  const sqlDetected = sql || sqlFiles.length > 0;
  const other = !sqlDetected && !azure && !cloudflare;

  return { sql: sqlDetected, azure, cloudflare, other, sqlFiles };
}

/* ── Server-side state helpers ── */

function makeHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  const token = getSessionToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

/**
 * Fetch PRs + testing state from the server.
 * GET /api/pr-list → { prs: [...], testingState: { approved, checkedSteps } }
 *
 * Backward-compat: if the response is a plain array (old backend), treat it
 * as prs-only with empty state.
 */
async function fetchPRsAndState(force = false) {
  if (!force && _cache && Date.now() - _cacheTime < CACHE_TTL) return _cache;

  const res = await fetch(getFunctionUrl('pr-list'), { headers: makeHeaders() });
  if (!res.ok) throw new Error(`pr-list ${res.status}`);

  const json = await res.json();

  // Backward compat: old backend returns a plain array
  const result = Array.isArray(json)
    ? { prs: json, testingState: { approved: [], checkedSteps: {} } }
    : {
        prs: json.prs || [],
        testingState: json.testingState || { approved: [], checkedSteps: {} },
      };

  _cache = result;
  _cacheTime = Date.now();
  return result;
}

/** POST the testing state to the server. */
async function saveTestingState(state) {
  await fetch(getFunctionUrl('pr-list'), {
    method: 'POST',
    headers: makeHeaders(),
    body: JSON.stringify(state),
  });
}

/* ── hook ── */

export function usePRTesting() {
  const [prs, setPrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [store, setStore] = useState({ approved: [], checkedSteps: {}, prTestBarEnabled: true });
  const saveTimer = useRef(null);

  /** Persist state to server (debounced). */
  const persist = useCallback((next) => {
    setStore(next);
    // Debounce server writes to avoid hammering on rapid checkbox clicks
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTestingState(next).catch(() => {});
    }, SAVE_DEBOUNCE);
  }, []);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const { prs: merged, testingState } = await fetchPRsAndState(force);
      setPrs(merged);
      setStore(testingState);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approvedSet = new Set(store.approved);

  /* ── PR approval ── */

  const approve = useCallback(
    (prNumber) => {
      const updated = {
        approved: [...new Set([...store.approved, prNumber])],
        checkedSteps: { ...store.checkedSteps },
      };
      delete updated.checkedSteps[prNumber];
      persist(updated);
    },
    [store, persist]
  );

  /* ── Sub-step toggling ── */

  const toggleStep = useCallback(
    (prNumber, stepText) => {
      const checked = store.checkedSteps[prNumber] || [];
      const isChecked = checked.includes(stepText);
      const updated = {
        ...store,
        checkedSteps: {
          ...store.checkedSteps,
          [prNumber]: isChecked ? checked.filter((t) => t !== stepText) : [...checked, stepText],
        },
      };
      persist(updated);
    },
    [store, persist]
  );

  const isStepChecked = useCallback(
    (prNumber, stepText) => {
      return (store.checkedSteps[prNumber] || []).includes(stepText);
    },
    [store.checkedSteps]
  );

  /* ── Get manual steps for a PR (parsed from body) ── */

  const getStepsForPR = useCallback(
    (prNumber) => {
      const pr = prs.find((p) => p.number === prNumber);
      return parseManualSteps(pr?.body);
    },
    [prs]
  );

  /* ── Get "how to test" steps for a PR ── */

  const getHowToTestForPR = useCallback(
    (prNumber) => {
      const pr = prs.find((p) => p.number === prNumber);
      return parseHowToTest(pr?.body);
    },
    [prs]
  );

  /* ── Category detection (from manual steps only) ── */

  const getCategoriesForPR = useCallback(
    (prNumber) => {
      const pr = prs.find((p) => p.number === prNumber);
      const steps = parseManualSteps(pr?.body);
      return detectCategories(steps);
    },
    [prs]
  );

  const untestedPRs = prs.filter((pr) => !approvedSet.has(pr.number));

  const setEnabled = useCallback(
    async (enabled) => {
      const next = { ...store, prTestBarEnabled: enabled };
      persist(next);
      // Flush immediately for the toggle (don't wait for debounce)
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      try {
        await fetch(getFunctionUrl('pr-list'), {
          method: 'POST',
          headers: makeHeaders(),
          body: JSON.stringify({ action: 'set-enabled', enabled }),
        });
      } catch {
        /* non-critical */
      }
    },
    [store, persist]
  );

  return {
    untestedPRs,
    loading,
    error,
    approve,
    toggleStep,
    isStepChecked,
    getStepsForPR,
    getHowToTestForPR,
    getCategoriesForPR,
    untestedCount: untestedPRs.length,
    prTestBarEnabled: store.prTestBarEnabled !== false,
    setEnabled,
    refresh: () => load(true),
  };
}
